import React, { useState, useEffect, useRef } from "react";
import { FaRegHeart } from "react-icons/fa";
import { FaCartPlus } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { addToCartThunk } from "../../redux/actions/cartActions";
import {
  toggleWishlistItem,
  fetchWishlist,
} from "../../redux/actions/wishlistAction";
import { Modal } from "../other";
import { getTrendingProducts } from "../../redux/actions/popularProductsAction";
import { checkStockAvailability } from "../../redux/actions/stockActions";
import { SwiperSlide, Swiper } from "swiper/react";

import {
  Navigation,
  Pagination,
  Scrollbar,
  A11y,
  Autoplay,
} from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/scrollbar";
import { Alert } from "../common/Alert";

function PopularProducts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userType = useSelector((state) => state.auth?.user?.user?.user_type);
  const wishlistItems = useSelector((state) => state.wishlist.items || []);
  const isAuthenticated = useSelector((state) => state.auth);
  const { loading, pop_products, error } = useSelector(
    (state) => state.popularProducts
  );
  const [activeFilter, setActiveFilter] = useState("All");
  const [filterItems, setFilterItems] = useState(["All"]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [subProducts, setSubProducts] = useState([]);
  const [quantities, setQuantities] = useState({});

  // Chunk array helper (to split products into groups of 2 for vertical pairs)
  const chunkArray = (arr, size) => {
    const result = [];

    for (let i = 0; i < arr.length; i += size) {
      if (arr[i].subProducts.length > 0) {
        result.push(arr.slice(i, i + size));
      }
    }
    return result;
  };

  // 1️⃣ Fetch trending products & wishlist on mount
  useEffect(() => {
    dispatch(getTrendingProducts());
    dispatch(fetchWishlist());
  }, [dispatch]);

  // 2️⃣ Build filters from the trending data
  useEffect(() => {
    if (pop_products?.length) {
      const cats = new Set(["All"]);
      pop_products.forEach((p) => {
        if (p.cat_name) cats.add(p.cat_name);
      });
      setFilterItems([...cats]);
    }
  }, [pop_products]);

  // 3️⃣ Map raw API shape to your UI shape
  useEffect(() => {
    if (!pop_products) return;
    const list = pop_products.map((product) => ({
      id: product.pro_id,
      title: product.product_name,
      type: product.product_sub_title || "N/A",
      category: product.cat_name,
      image: product.product_primary_img,
      subProducts: (product.SubProducts || []).map((sub) => ({
        sub_product_id: sub.sub_pro_id,
        size: sub.size,
        color: sub.color_id,
        vendorPrice: sub.vendor_product_price,
        customerPrice: sub.customer_product_price,
        set_price: sub.set_price,
        discount: sub.customer_product_discount,
        subImage: sub.sub_pro_img,
        no_of_pcs: sub.no_of_pcs,
        stock: sub.no_of_stock,
        type: sub.selling_type,
        total_ordered_quantity: sub.total_ordered_quantity,
        selling_type: sub.selling_type,
        no_of_pcs: sub.no_of_pcs,
      })),
    }));
    setProducts(list);
  }, [pop_products]);

  const chunkProducts = (arr, size) => {
    const chunks = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  };

  // 4️⃣ Handlers for cart modal
  const handleAddToCartClick = (e, product) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
    }
    if (product?.subProducts?.length) {
      setCurrentProduct(product);
      setSubProducts(product.subProducts);
      const initial = {};
      product.subProducts.forEach((s) => {
        initial[s.sub_product_id] = 1;
      });
      setQuantities(initial);
      setIsModalOpen(true);
    } else {
      toast.info("No sub-products available for this item.");
    }
  };

  const incrementQuantity = (sub) => {
    const sub_product_id = sub.sub_product_id;
    const quantity = (quantities[sub_product_id] || 1) + 1;

    // First, log what we're sending

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          toast.info("Insufficient Stock !!!");
        } else {
          // Stock is available, increment quantity
          setQuantities((prev) => ({
            ...prev,
            [sub_product_id]: quantity,
          }));
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  const decrementQuantity = (id) => {
    setQuantities((q) => ({ ...q, [id]: q[id] > 1 ? q[id] - 1 : 1 }));
  };

  // const handleAddSubProductToCart = (sub) => {
  //   const product_id = currentProduct.id;
  //   const sub_product_id = sub.sub_product_id;
  //   const quantity = quantities[sub_product_id] || 1;
  //   if (!product_id || !sub_product_id) return;
  //   dispatch(addToCartThunk({ product_id, sub_product_id, quantity }));
  // };

  const handleAddSubProductToCart = (sub) => {
    const product_id = currentProduct.id;
    const sub_product_id = sub.sub_product_id;
    const quantity = quantities[sub_product_id] || 1;

    dispatch(checkStockAvailability(sub_product_id, quantity))
      .then((response) => {
        // Log the entire response to see its structure

        // The response structure is likely different than expected
        // The data might be directly in response.data, not in response.payload.data
        const data = response.data || {};

        // Check if there's stock available
        if (
          data.data?.status === "out_of_stock" ||
          data.data?.available_stock < data.data?.required_quantity
        ) {
          Alert.ErrorAlert("Insufficient stock!");
        } else {
          // Stock is available, increment quantity
          dispatch(
            addToCartThunk({
              product_id: product_id,
              sub_product_id,
              quantity,
            })
          );
        }
      })
      .catch((error) => {
        // Log the error for debugging

        // Display a more specific error message if available
        const errorMessage =
          error.message || "Failed to check stock. Please try again.";
        toast.error(errorMessage);
      });
  };

  // 5️⃣ Other handlers
  const handleQuickViewClick = (product) => {
    setSelectedProduct(product);
    navigate(`/ProductsDetails/${product.id}`);
  };
  const handleWishlist = (e, productId) => {
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
    }
    if (!productId) return;
    dispatch(toggleWishlistItem(productId));
  };
  const handleFilterClick = (filter) => setActiveFilter(filter);

  // 6️⃣ Final filter
  const filteredProducts =
    activeFilter === "All"
      ? products
      : products.filter((p) => p.category === activeFilter);

  const chunkedProducts = chunkArray(filteredProducts, 2);

  if (error) return <p className="text-center text-danger mt-5">{error}</p>;
  return (
    <section>
      <div className="container flex flex-col gap-4">
        <div className="flex flex-col gap-3 md:flex-row justify-between items-center">
          {/* Title Section */}
          <h2 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl fw-bold">
            Most Popular Products
          </h2>

          {/* Filter Section */}
          <div className="border border-dark rounded-5 px-3 w-full md:w-1/2">
            <ul className="list-unstyled flex justify-content-between py-2 gap-1 align-items-center">
              {filterItems.map((filter, idx) => (
                <li
                  key={idx}
                  className={`filter-item text-center font-weight-medium px-3 rounded-xl hover:bg-theme-color cursor-pointer ${
                    activeFilter === filter && "bg-theme-color"
                  }`}
                  onClick={() => handleFilterClick(filter)}
                >
                  {filter}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {loading ? (
          <p className="text-center mt-5">Loading...</p>
        ) : (
          <>
            {filteredProducts.length === 0 ? (
              <p className="text-center">
                No products found for "{activeFilter}"
              </p>
            ) : (
              <Swiper
                modules={[Navigation, Pagination, Scrollbar, A11y, Autoplay]}
                spaceBetween={24}
                slidesPerView={4}
                // navigation
                // pagination={{ clickable: true }}
                scrollbar={{ draggable: true }}
                autoplay={{ delay: 5000, disableOnInteraction: false }}
                // loop={true}
                breakpoints={{
                  0: { slidesPerView: 2, spaceBetween: 10 },
                  640: { slidesPerView: 3, spaceBetween: 15 },
                  768: { slidesPerView: 4, spaceBetween: 20 },
                }}
                className="pb-5 w-full"
              >
                {chunkedProducts.map((pair, idx) => (
                  <SwiperSlide key={pair.map((p) => p.id).join("-")}>
                    <div className="flex flex-col gap-6">
                      {pair.map((product) => {
                        const isWishlisted = wishlistItems.some(
                          (item) =>
                            (item.product?.pro_id || item.pro_id) === product.id
                        );

                        return (
                          <ProductCard
                            key={product.id}
                            product={product}
                            isAuthenticated={isAuthenticated}
                            isWishlisted={isWishlisted}
                            handleAddToCartClick={handleAddToCartClick}
                            handleQuickViewClick={handleQuickViewClick}
                            handleWishlist={handleWishlist}
                            userType={userType}
                          />
                        );
                      })}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </>
        )}
        {/* </div> */}
      </div>

      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Select Sub Product"
        >
          <div className="flex flex-col gap-4 max-h-[70vh] overflow-auto">
            {subProducts.map((sub, idx) => (
              <div
                key={idx}
                className="w-full border rounded-xl p-4 shadow hover:shadow-lg transition flex flex-row items-center gap-4"
              >
                <img
                  loading="lazy"
                  src={sub.subImage}
                  alt={`sub-product-${idx}`}
                  className="w-32 h-44 object-cover hover:scale-110 transition-transform ease-in-out duration-500 cursor-pointer rounded-lg flex-shrink-0"
                />
                <div className="flex flex-col justify-between flex-grow h-full">
                  <div className="space-y-1">
                    <p>
                      <strong>Color:</strong> {sub.color}
                    </p>
                    <p>
                      <strong>Size:</strong> {sub.size}
                    </p>
                    <p>
                      <strong>Price:</strong> ₹
                      {userType === "vendor"
                        ? sub.vendorPrice
                        : sub.customerPrice}
                    </p>
                  </div>
                  <div className="self-start flex items-center mt-2 gap-2 ">
                    <div className="border border-gray-300 rounded-full overflow-hidden">
                      <button
                        onClick={() => decrementQuantity(sub.sub_product_id)}
                        className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                        disabled={quantities[sub.sub_product_id] <= 1}
                      >
                        -
                      </button>
                      <span className="px-4 py-1 select-none">
                        {quantities[sub.sub_product_id] || 1}
                      </span>
                      <button
                        onClick={() => incrementQuantity(sub)}
                        className="px-2 lg:px-3 py-1 bg-gray-200 hover:bg-gray-300 cursor-pointer"
                      >
                        +
                      </button>
                    </div>

                    {userType === "vendor" ? (
                      sub.selling_type == "set" ? (
                        <span className="ml-2">
                          {quantities[sub.sub_product_id] <= 1 ? "Set" : "Sets"}{" "}
                        </span>
                      ) : (
                        <span className="ml-2">
                          {quantities[sub.sub_product_id] <= 1
                            ? "Piece"
                            : "Pieces"}
                        </span>
                      )
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between mt-2 gap-2 flex-wrap">
                    {userType === "vendor" &&
                    sub.selling_type == "set" &&
                    sub.no_of_pcs ? (
                      <p className="italic text-sm text-red-600 font-semibold whitespace-nowrap min-w-[180px]">
                        {` ${quantities[sub.sub_pro_id] || 1} Set = ${
                          sub.no_of_pcs * (quantities[sub.sub_product_id] || 1)
                        } pieces`}{" "}
                        (₹
                        {sub.set_price * (quantities[sub?.sub_product_id] || 1)}
                        )
                      </p>
                    ) : (
                      <div className="min-w-[180px]" />
                    )}
                    {isAuthenticated ? (
                      <div className="flex flex-col gap-1 w-full sm:w-fit">
                        <button
                          className="btn theme-outline-btn disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={() => handleAddSubProductToCart(sub)}
                          disabled={sub.stock <= 0}
                        >
                          Add Item
                        </button>
                        {sub.stock <= 0 && (
                          <p className="text-red-600 text-sm font-semibold">
                            Out of Stock
                          </p>
                        )}
                      </div>
                    ) : (
                      navigate("/login")
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </section>
  );
}

const ProductCard = ({
  product = {},
  isAuthenticated = false,
  isWishlisted = false,
  handleAddToCartClick,
  handleQuickViewClick,
  handleWishlist,
  userType,
}) => {
  return (
    <div
      className="cursor-pointer select-none"
      onClick={() => handleQuickViewClick(product)}
    >
      <div
        onClick={() => handleQuickViewClick(product)}
        key={product.id}
        className="cursor-pointer relative"
      >
        <div className="aspect-[3/4] overflow-hidden rounded-3xl">
          <img
            loading="lazy"
            src={product.image}
            alt={product.title}
            className="object-fit size-full"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://via.placeholder.com/150?text=No+Image";
            }}
          />
        </div>
        <div className="d-flex align-items-center justify-content-between gap-2 mt-1 flex-wrap">
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-truncate pb-0 fs-5 fs-6">
              <b>{product.title}</b>
            </span>
            <small className="text-truncate pt-0 fw-bold -mb-2">
              {product.type}
            </small>
          </div>
          {isAuthenticated && (
            <div className="d-flex align-items-center justify-content-end">
              <div className="px-2 py-1 text-base font-semibold rounded-xl bg-white">
                {userType === "vendor"
                  ? product.subProducts[0]?.vendorPrice
                  : product.subProducts[0]?.price}
                <span> ₹</span>
              </div>
            </div>
          )}
        </div>
        <div className="position-absolute top-2 end-0 mr-2 d-flex flex-column group-hover:-translate-y-5 lg:pr-7">
          <button
            onClick={(e) => handleWishlist(e, product.id)}
            className={`badge rounded-circle p-2 fs-6 mt-2 transition-colors ${
              isWishlisted ? "bg-red-500 text-white" : "bg-tealish text-white"
            }`}
            title={isWishlisted ? "Remove from Wishlist" : "Add to Wishlist"}
          >
            <FaRegHeart />
          </button>
          <button
            onClick={(e) => handleAddToCartClick(e, product)}
            className="badge rounded-circle bg-tealish text-white p-2 fs-6 mt-2"
          >
            <FaCartPlus />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PopularProducts;
